import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { auth } from '../../../firebase';
import HarbourBrandLockup from '../../components/harbour/HarbourBrandLockup';
import HarbourIcon from '../../components/harbour/HarbourIcon';
import '../../components/harbour/harbour.css';
import './family.css';

/*
 * Family portal shell (SEN & ND Access Portal, TDI-53 parent-or-carer side).
 * Mobile-first, one child at a time. The top bar shows the brand lockup on the
 * home page and a Back link on every inner page; the user menu sits on the
 * right everywhere, labelled "Menu" with the signed-in user's monogram.
 */
function initialsOf(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

export default function FamilyPortalShell({ userName, back, title, children, maxWidth = 'default' }) {
  const navigate = useNavigate();
  // The phone app bar takes a soft shadow while the page is scrolled under
  // it, and loses it back at the top (TDI-697).
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // With a title, the bar shows it once the page heading has scrolled away
  // (TDI-826 part 3): the page marks its heading with
  // data-family-title-marker, and the bar's copy appears while that marker is
  // off screen — hidden from screen readers, because the page heading already
  // names the page.
  const [headingAway, setHeadingAway] = useState(false);
  useEffect(() => {
    if (!title) return undefined;
    const marker = document.querySelector('[data-family-title-marker]');
    if (!marker || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setHeadingAway(!entry.isIntersecting),
      { rootMargin: '-56px 0px 0px 0px' }
    );
    observer.observe(marker);
    return () => observer.disconnect();
  }, [title]);
  return (
    <div className="harbour harbour-page family-page">
      <header className={`family-top-bar${scrolled ? ' family-top-bar--scrolled' : ''}`}>
        {back ? (
          <Link to={back.to} className="family-back-link">
            <HarbourIcon name="chevron-left" size={20} />
            {back.label || 'Back'}
          </Link>
        ) : (
          <HarbourBrandLockup />
        )}
        {title && headingAway && (
          <span className="family-top-bar__title" aria-hidden="true">{title}</span>
        )}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" className="family-user-menu" aria-label="Menu">
              <span className="harbour-monogram" aria-hidden="true">{initialsOf(userName)}</span>
              {/* Mobile shows the word Menu; desktop shows the signed-in name
                  (Harbour/User menu). CSS swaps them, never both. */}
              <span className="family-user-menu__word">Menu</span>
              <span className="family-user-menu__name">{userName}</span>
              <HarbourIcon name="chevron-down" size={20} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="harbour harbour-card z-50 min-w-[200px] p-2"
            >
              <DropdownMenu.Item
                className="harbour-nav-item w-full cursor-pointer outline-none data-[highlighted]:bg-[var(--harbour-secondary-hover-bg)]"
                onSelect={() => navigate('/rms/family/children', { state: { stay: true } })}
              >
                Your children
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="harbour-nav-item w-full cursor-pointer outline-none data-[highlighted]:bg-[var(--harbour-secondary-hover-bg)]"
                onSelect={async () => {
                  try { await auth.signOut(); } catch (_) { /* no-op */ }
                  navigate('/rms/family/sign-in?signedout=1');
                }}
              >
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </header>
      <main
        className={[
          'family-main',
          maxWidth === 'wide' ? 'family-main--wide' : '',
          maxWidth === 'full' ? 'family-main--full' : '',
        ].filter(Boolean).join(' ')}
      >
        {children}
      </main>
    </div>
  );
}
